import {
  type BaseConnection,
  Button,
  type CapabilityWizardSpec,
  DEFAULT_DISABLED_REASON,
  cn,
  localize,
  resolveConnectNoun,
} from "@ando/ui";
import { useLingui } from "@lingui/react/macro";
import { ChevronRight, Plug } from "lucide-react";
import type * as React from "react";

import { vendorIcon } from "@/features/connectors/components/vendorIcons";

/**
 * ConnectProviderCards — the single, DRY "connect a provider" surface.
 *
 * A grid of one-click provider cards driven by ``spec.vendors``. Used
 * everywhere the user can add an account so the connectable providers are
 * ALWAYS visible, never hidden behind a dropdown:
 *   • empty-state hero ("Collega il tuo primo account …")
 *   • "Aggiungi un altro account" section under the connected list
 *
 * Same shape for every capability (Calendar / Email / Drive / …) because they
 * all expose ``vendors`` — one component, one interaction, KISS + DRY.
 *
 * Zero-vendor specs (capability == vendor) fall back to a single direct
 * button. Gated vendors render disabled with their reason. The card click
 * routes straight through ``onConnect(vendorId)`` — the caller owns the flow.
 */
export interface ConnectProviderCardsProps {
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  readonly onConnect: (vendorId: string) => void;
  /** Extra classes for the grid ``<ul>`` — lets callers tune columns/width. */
  readonly className?: string;
}

export const ConnectProviderCards: React.FC<ConnectProviderCardsProps> = ({
  spec,
  onConnect,
  className,
}) => {
  const { i18n } = useLingui();
  const vendors = spec.vendors ?? [];

  // Specs that don't enumerate vendors (capability == vendor): one direct CTA.
  if (vendors.length === 0) {
    const { addLabel } = resolveConnectNoun(spec);
    return (
      <Button variant="primary" size="md" aria-label={addLabel} onClick={() => onConnect("")}>
        <Plug aria-hidden />
        {`+ ${addLabel}`}
      </Button>
    );
  }

  return (
    <ul className={cn("grid grid-cols-1 gap-2.5 sm:grid-cols-2", className)}>
      {vendors.map((vendor) => {
        const label = localize(vendor.label, i18n);
        const brand = vendorIcon(vendor.id, "h-5 w-5");
        const icon = brand ?? vendor.icon ?? null;
        const locked = vendor.disabled === true;
        // Subtitle only when it carries real info (gated reason or a spec
        // description) — no filler; without it the card is a clean single line.
        const subtitle = locked
          ? vendor.disabledReason
            ? localize(vendor.disabledReason, i18n)
            : DEFAULT_DISABLED_REASON
          : vendor.description
            ? localize(vendor.description, i18n)
            : null;
        return (
          <li key={vendor.id}>
            <button
              type="button"
              disabled={locked}
              aria-disabled={locked || undefined}
              onClick={() => {
                if (!locked) onConnect(vendor.id);
              }}
              data-testid={`connect-vendor-${vendor.id}`}
              className={cn(
                "group flex w-full items-center gap-3 rounded-xl border border-hairline bg-card/60 p-3.5 text-left shadow-sm transition duration-normal ease-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                locked
                  ? "cursor-not-allowed opacity-60"
                  : "hover:-translate-y-px hover:bg-card/80 hover:shadow-md hover:ring-1 hover:ring-ando-orange-soft",
              )}
            >
              <span
                className={cn(
                  "grid h-9 w-9 shrink-0 place-items-center rounded-md bg-white/70 ring-1 ring-hairline",
                  locked ? "grayscale" : "",
                )}
              >
                {icon}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium text-[13px] text-ink-1">{label}</span>
                {subtitle ? (
                  <span className="truncate text-[11px] text-ink-3">{subtitle}</span>
                ) : null}
              </span>
              {locked ? null : (
                <ChevronRight
                  size={15}
                  aria-hidden
                  className="shrink-0 text-ink-4 transition-transform group-hover:translate-x-0.5 group-hover:text-ando-orange"
                />
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
};
ConnectProviderCards.displayName = "ConnectProviderCards";
